import classNames from "classnames";
import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import WorkflowTemplateDetailsDialog from "./WorkflowTemplateDetailsDialog";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import { IntegrationIcon } from "components/icons/IntegrationIcon";
import { ModularIcon } from "components/icons/ModularIcon";
import { ReadyToUseIcon } from "components/icons/ReadyToUseIcon";
import ScheduledIcon from "components/icons/ScheduledIcon";
import { SuccessIcon } from "components/icons/SuccessIcon";
import layoutStyle from "components/layout/layout.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import Tooltip from "components/tooltip/Tooltip";
import publishTemplateStyle from "components/workflows/workflow-templates/published-workflow-template.scss";
import { LaunchType } from "domain/workflows";
import { TemplateTableData } from "domain/workflowTemplates";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { ConflictBehavior, templateService, WorkflowTemplateSaveResponse } from "services/workflows/TemplateService";
import {
    ManifestWorkflowEditor,
    Profile,
    profileToNameMap,
    WorkflowEditorV3SupportedEntry,
    workflowService,
} from "services/workflows/WorkflowService";
import { useTheme } from "utils/useTheme";

import assetGradingIcon from "assets/images/icons/assetGradingIcon.svg";
import lockIcon from "assets/images/icons/darkGreyLockIcon.svg";

import testIds from "testIds.json";

interface Props<T extends TemplateTableData> {
    data: T[];
    loaded: boolean;
    setCount: React.Dispatch<React.SetStateAction<number>>;
    workflowEditors: ManifestWorkflowEditor[];
    workflowEditorV3Supported: WorkflowEditorV3SupportedEntry[];
}
const DOT = " • ";

export interface TemplateDetailsDialog {
    show: boolean;
    details?: TemplateTableData;
}

interface ConflictsResponseDto {
    conflicting_template: string | null;
    conflicting_sub_workflows: string[];
}

export interface ConflictsResponse {
    conflictingTemplate: string | null;
    conflictingSubWorkflows: string[];
}

export interface ActiveWorkflowDialog {
    show: boolean;
    workflowUuid: string;
}

export interface RequestFailure {
    message: string;
    title: string;
    scheduleWarning?: string;
}

export function getTemplateTypeBadge(
    templateType: string,
    t: TFunction,
    readyToUseSkyBlueColor: string,
    modularPurpleColor: string,
    integrationGreenColor: string
): JSX.Element {
    if (templateType === "Ready to use") {
        return (
            <Tooltip content={t("workflowTemplate.workflowTemplateDetailsDialog.tooltip.modular")}>
                <div className={publishTemplateStyle.readyToUseContainer}>
                    <ReadyToUseIcon linecolor={readyToUseSkyBlueColor} />
                    <div className={publishTemplateStyle.textSpace}>{templateType.replace(/ /g, "-")}</div>
                </div>
            </Tooltip>
        );
    }
    if (templateType === "Modular") {
        return (
            <Tooltip content={t("workflowTemplate.workflowTemplateDetailsDialog.tooltip.readyToUse")}>
                <div className={publishTemplateStyle.modularContainer}>
                    <ModularIcon color={modularPurpleColor} />
                    <div className={publishTemplateStyle.textSpace}>{templateType}</div>
                </div>
            </Tooltip>
        );
    }
    if (templateType === "Integration") {
        return (
            <Tooltip content={t("workflowTemplate.workflowTemplateDetailsDialog.tooltip.integration")}>
                <div className={publishTemplateStyle.integrationContainer}>
                    <IntegrationIcon color={integrationGreenColor} />
                    <div className={publishTemplateStyle.textSpace}>{templateType}</div>
                </div>
            </Tooltip>
        );
    }
    return <></>;
}

function PublishedWorkflowTemplate<D extends TemplateTableData>(props: Props<D>): JSX.Element {
    if (!props.loaded) {
        return <LoadingIndicator isView />;
    }
    const theme = useTheme();
    const { t } = useTranslation();
    const [templateDetailsDialog, setTemplateDetailsDialog] = React.useState<TemplateDetailsDialog>({
        show: false,
    });
    const [loading, setLoading] = React.useState<boolean>(false);
    const [addedBadge, setAddedBadge] = React.useState<boolean>(false);
    const [updatePriority, setUpdatePriority] = React.useState<boolean>(true);
    const [showActiveWorkflowDialog, setShowActiveWorkflowDialog] = React.useState<ActiveWorkflowDialog>({
        show: false,
        workflowUuid: "",
    });
    const [conflictsException, setConflictsException] = React.useState<ConflictsResponse | undefined>(undefined);
    const [activeWorkflowLoading, setActiveWorkflowLoading] = React.useState<boolean>(false);
    const [requestFailure, setRequestFailure] = React.useState<RequestFailure | null>(null);
    const setSavedWorkflowActive = () => {
        setActiveWorkflowLoading(true);
        const abortController = new AbortController();
        const serviceCall =
            Profile.BMP === templateDetailsDialog.details?.profile
                ? workflowService.setActive(showActiveWorkflowDialog.workflowUuid, true, abortController)
                : workflowService.setDefault(showActiveWorkflowDialog.workflowUuid, abortController);
        serviceCall
            .then(() => {
                showSuccessNotificationToast(true, Profile.BMP === templateDetailsDialog.details?.profile);
            })
            .catch(() => {
                setRequestFailure({
                    message:
                        Profile.BMP === templateDetailsDialog.details?.profile
                            ? t("workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.active.error.message")
                            : t(
                                  "workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.default.error.message"
                              ),
                    title:
                        Profile.BMP === templateDetailsDialog.details?.profile
                            ? t("workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.active.error.title")
                            : t("workflowTemplate.workflowTemplateDetailsDialog.setTemplateStatus.default.error.title"),
                });
            })
            .finally(() => {
                setActiveWorkflowLoading(false);
                setShowActiveWorkflowDialog({
                    show: false,
                    workflowUuid: "",
                });
            });
    };

    const showSuccessNotificationToast = (active: boolean, isBmpTemplate: boolean) => {
        let message = t("workflowTemplate.workflowTemplateDetailsDialog.successToast.success", {
            templateName: templateDetailsDialog.details?.name,
        });
        if (active) {
            message +=
                " " +
                (isBmpTemplate
                    ? t("workflowTemplate.workflowTemplateDetailsDialog.successToast.active")
                    : t("workflowTemplate.workflowTemplateDetailsDialog.successToast.default"));
        }
        return toast(
            <SuccessIcon
                successClass={layoutStyle.customToastSuccessIcon}
                color={theme.contentBackgroundColor}
                text={message}
            />,
            {
                closeButton: (closeToastProps) => (
                    <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                ),
                className: layoutStyle.customToastSuccessMessage,
            }
        );
    };

    const saveTemplate = (conflictBehavior: ConflictBehavior, renameTargets?: Record<string, string>) => {
        if (templateDetailsDialog.details) {
            setLoading(true);
            const abortController = new AbortController();

            templateService
                .save(templateDetailsDialog.details.uuid, conflictBehavior, renameTargets, abortController)
                .then((response: WorkflowTemplateSaveResponse) => {
                    setRequestFailure(null);
                    setLoading(false);
                    setAddedBadge(true);
                    setUpdatePriority(false);
                    !hasTenantCookie() && templateDetailsDialog.details?.launchType !== LaunchType.MANUAL
                        ? setShowActiveWorkflowDialog({
                              show: true,
                              workflowUuid: response.workflowUuid,
                          })
                        : showSuccessNotificationToast(false, Profile.BMP === templateDetailsDialog.details?.profile);
                })
                .catch((exception) => {
                    try {
                        if (!abortController.signal.aborted) {
                            setConflictsException(toConflictsResponse(JSON.parse(exception.message)));
                            setRequestFailure({
                                message: t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.message"),
                                title: t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.title"),
                                scheduleWarning: templateDetailsDialog.details?.schedule
                                    ? t("workflowTemplate.workflowTemplateDetailsDialog.error.conflict.scheduleMessage")
                                    : undefined,
                            });
                            setAddedBadge(false);
                            setUpdatePriority(true);
                            setShowActiveWorkflowDialog({
                                show: false,
                                workflowUuid: "",
                            });
                        }
                    } catch {
                        setRequestFailure({
                            title: t("workflowTemplate.workflowTemplateDetailsDialog.error.generic.title"),
                            message: t("workflowTemplate.workflowTemplateDetailsDialog.error.generic.message"),
                        });
                        setConflictsException(undefined);
                        setAddedBadge(false);
                        setUpdatePriority(true);
                        setShowActiveWorkflowDialog({
                            show: false,
                            workflowUuid: "",
                        });
                    }
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    return (
        <div
            className={publishTemplateStyle.gridContainer}
            data-testid={testIds.workArea.workflowTemplates.primaryView.cardGrid.itself}
        >
            {props.data.map((item, index) => (
                <div
                    key={index}
                    className={publishTemplateStyle.gridItem}
                    data-testid={testIds.workArea.workflowTemplates.primaryView.cardGrid.card}
                    onClick={() => {
                        setTemplateDetailsDialog({ show: true, details: item });
                        setRequestFailure(null);
                    }}
                >
                    <div className={publishTemplateStyle.iconContainer}>
                        {getTemplateTypeBadge(
                            item.templateType,
                            t,
                            theme.readyToUseSkyBlueColor,
                            theme.modularPurpleColor,
                            theme.integrationGreenColor
                        )}
                        <div className={publishTemplateStyle.leftAlignBadge}>
                            {item.update ? (
                                <div className={publishTemplateStyle.added}>
                                    <StatusBadge
                                        values={[
                                            {
                                                status: Status.WARNING,
                                                title: t("workflowTemplate.workflowTemplateDetailsDialog.update"),
                                            },
                                        ]}
                                        tooltip={true}
                                    />
                                </div>
                            ) : (
                                item.saved && (
                                    <div className={publishTemplateStyle.added}>
                                        <StatusBadge
                                            values={[
                                                {
                                                    status: Status.SUCCESS,
                                                    title: t("workflowTemplate.workflowTemplateDetailsDialog.added"),
                                                },
                                            ]}
                                            tooltip={true}
                                        />
                                    </div>
                                )
                            )}
                            {item.secured && (
                                <Tooltip content={t("Common.secured")}>
                                    <div
                                        className={classNames(
                                            publishTemplateStyle.added,
                                            publishTemplateStyle.iconSpacing
                                        )}
                                        data-testid={
                                            testIds.workArea.workflowTemplates.primaryView.cardGrid.securedIcon
                                        }
                                    >
                                        <img src={lockIcon} alt="secured" style={{ width: 14, height: 14 }} />
                                    </div>
                                </Tooltip>
                            )}
                            {item.schedule && (
                                <Tooltip content={t("Common.scheduled")}>
                                    <div
                                        className={classNames(
                                            publishTemplateStyle.added,
                                            publishTemplateStyle.iconSpacing
                                        )}
                                        data-testid={
                                            testIds.workArea.workflowTemplates.primaryView.cardGrid.scheduledIcon
                                        }
                                    >
                                        <ScheduledIcon color={theme.textGreyColor} size={14} />
                                    </div>
                                </Tooltip>
                            )}
                            {item.assetGrading && (
                                <Tooltip content={t("workflowTemplate.assetGrading")}>
                                    <div
                                        className={classNames(
                                            publishTemplateStyle.added,
                                            publishTemplateStyle.iconSpacing
                                        )}
                                    >
                                        <img
                                            src={assetGradingIcon}
                                            alt="asset grading"
                                            style={{ width: 14, height: 14 }}
                                        />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <div className={publishTemplateStyle.container}>
                        <div className={publishTemplateStyle.deviceItem}>{item.name}</div>
                        <div className={publishTemplateStyle.versionContainer}>
                            <div className={publishTemplateStyle.textstyle}>
                                {profileToNameMap.get(item.profile as Profile)} {item.editorVersion}
                                {item.useCases.map((data: string) => (
                                    <React.Fragment key={data}>
                                        {DOT} {data}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            ))}

            <WorkflowTemplateDetailsDialog
                showTemplateDetailsDialog={templateDetailsDialog}
                setShowTemplateDetailsDialog={setTemplateDetailsDialog}
                setAddedBadge={setAddedBadge}
                setCount={props.setCount}
                loading={loading}
                conflictsException={conflictsException}
                addedBadge={addedBadge}
                saveTemplate={saveTemplate}
                showActiveWorkflowDialog={showActiveWorkflowDialog}
                setShowActiveWorkflowDialog={setShowActiveWorkflowDialog}
                setSavedWorkflowActive={setSavedWorkflowActive}
                showSuccessNotificationToast={showSuccessNotificationToast}
                activeWorkflowLoading={activeWorkflowLoading}
                workflowEditors={props.workflowEditors}
                workflowEditorV3Supported={props.workflowEditorV3Supported}
                previewOnly={false}
                updatePriority={updatePriority}
                requestFailure={requestFailure}
            />
        </div>
    );
}

export const toConflictsResponse = (dto: ConflictsResponseDto): ConflictsResponse => {
    return {
        conflictingTemplate: dto.conflicting_template,
        conflictingSubWorkflows: dto.conflicting_sub_workflows,
    };
};
export default PublishedWorkflowTemplate;
